import type { TargetBuilder } from "./blueprintTypes";

export type ProposedAssumptionsInput = {
  appIdea: string;
  title?: string;
  targetBuilder: TargetBuilder;
};

const MAX_ASSUMPTION_LENGTH = 500;
const MAX_ASSUMPTIONS = 50;

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

function hasAdmin(idea: string): boolean {
  return /\badmin\b|\badministrat/i.test(idea);
}

function hasPublicUsers(idea: string): boolean {
  return /\bpublic\b|\bvisitor[s]?\b|\banyone\b|\busers?\b|\bcustomer[s]?\b|\bclient[s]?\b/i.test(
    idea,
  );
}

function hasPublicNoLogin(idea: string): boolean {
  return /without log|without sign|no log.?in|no account|don't log|not log|publicly access/i.test(
    idea,
  );
}

function hasSearch(idea: string): boolean {
  return /\bsearch\b/i.test(idea);
}

function hasFilter(idea: string): boolean {
  return /\bfilter\b/i.test(idea);
}

function hasPrint(idea: string): boolean {
  return /\bprint\b/i.test(idea);
}

function hasWebsiteLinks(idea: string): boolean {
  return /\bwebsite link[s]?\b|\bexternal link[s]?\b|\burl\b|\bweb link[s]?\b/i.test(
    idea,
  );
}

function hasCrudRecords(idea: string): boolean {
  return /\badd\b|\bedit\b|\bdelete\b|\bcreate\b|\bupdate\b|\bmanage\b|\bcrud\b|\bdirectory\b|\blibrary\b|\bcatalogue\b|\bcatalog\b|\brecord[s]?\b/i.test(
    idea,
  );
}

function hasAi(idea: string): boolean {
  return /\b(ai\b|artificial intelligence|openai|gpt|llm|machine learning|generat(?:e|ed|ing) (?:content|text|image|output))/i.test(
    idea,
  );
}

function hasPayments(idea: string): boolean {
  return /\bpay\b|\bpayment[s]?\b|\bstripe\b|\bcheckout\b|\binvoice\b|\bsubscription\b|\bbilling\b/i.test(
    idea,
  );
}

function hasBooking(idea: string): boolean {
  return /\bbook\b|\bbooking\b|\bappointment\b|\bschedule[s]?\b|\bslot[s]?\b/i.test(
    idea,
  );
}

function hasExport(idea: string): boolean {
  return /\bexport\b|\bcsv\b|\bdownload\b/i.test(idea);
}

// ---------------------------------------------------------------------------
// Product name inference (Phase 3H: smart derivation for generic/placeholder titles)
// ---------------------------------------------------------------------------

const GENERIC_TITLE_PATTERNS = [
  /^my blueprints? draft$/i,
  /^untitled$/i,
  /^new blueprint$/i,
  /^app idea draft$/i,
  /^draft$/i,
  /^blueprint\s*\d*$/i,
  /^new app$/i,
  /^untitled blueprint$/i,
];

function isGenericTitle(title: string): boolean {
  return GENERIC_TITLE_PATTERNS.some((p) => p.test(title.trim()));
}

function deriveProductNameFromIdea(idea: string): string {
  if (/affiliate.*advertising.*source[s]?/i.test(idea)) return "Affiliate Advertising Sources Library";
  if (/affiliate/i.test(idea) && /advertising source[s]?/i.test(idea)) return "Affiliate Advertising Sources Library";
  if (/client.*portal.*coach/i.test(idea) || /coach.*client.*portal/i.test(idea)) return "Client Portal for Coaches";
  if (/booking.*service.*provider/i.test(idea) || /service.*provider.*booking/i.test(idea)) return "Service Provider Booking Tool";
  if (/lead.*magnet.*idea.*gen/i.test(idea) || /lead.*magnet.*gen/i.test(idea)) return "Lead Magnet Idea Generator";
  return "";
}

function inferProductName(title: string | undefined, appIdea: string): string {
  const raw = title?.trim() ?? "";
  if (!raw || raw.length <= 2 || raw.length >= 120) return "";
  if (isGenericTitle(raw)) {
    const derived = deriveProductNameFromIdea(appIdea);
    return derived || "";
  }
  return raw;
}

// ---------------------------------------------------------------------------
// Target builder label
// ---------------------------------------------------------------------------

const TARGET_BUILDER_LABELS: Record<TargetBuilder, string> = {
  universal: "Universal Builder",
  replit: "Replit",
  lovable: "Lovable",
  base44: "Base44",
  bolt: "Bolt",
  abacus: "Abacus",
  cursor: "Cursor",
  v0: "v0",
  claude_code: "Claude Code",
};

function targetBuilderLabel(targetBuilder: TargetBuilder): string {
  return TARGET_BUILDER_LABELS[targetBuilder] ?? targetBuilder;
}

// ---------------------------------------------------------------------------
// Main generator
// ---------------------------------------------------------------------------

export function generateProposedAssumptions(
  input: ProposedAssumptionsInput,
): string[] {
  const idea = input.appIdea;
  const adminPresent = hasAdmin(idea);
  const publicPresent = hasPublicUsers(idea);
  const noLogin = hasPublicNoLogin(idea);
  const searchPresent = hasSearch(idea);
  const filterPresent = hasFilter(idea);
  const printPresent = hasPrint(idea);
  const linksPresent = hasWebsiteLinks(idea);
  const crudPresent = hasCrudRecords(idea);
  const aiPresent = hasAi(idea);
  const paymentsPresent = hasPayments(idea);
  const bookingPresent = hasBooking(idea);
  const exportPresent = hasExport(idea);
  const isReplit = input.targetBuilder === "replit";

  const assumptions: string[] = [];

  // 1. Product name
  const productName = inferProductName(input.title, input.appIdea);
  if (productName) {
    assumptions.push(`Product name: ${productName}.`);
  }

  // 2. Roles
  if (adminPresent && publicPresent) {
    assumptions.push(
      "There will be two roles: public visitor and admin. Public visitors do not log in.",
    );
  } else if (adminPresent) {
    assumptions.push("Admin must log in securely to manage the app.");
  } else if (publicPresent) {
    assumptions.push("Public users can access the app.");
  }

  // 3. Public access detail
  if (publicPresent && noLogin) {
    assumptions.push("Public visitors do not need to create an account or log in to access content.");
  }

  // 4. Admin secure login
  if (adminPresent) {
    assumptions.push(
      "Admin must log in with secure credentials. Admin access must be behind a real login form, not just a secret path or URL.",
    );
  }

  // 5. Public actions
  const publicActions: string[] = [];
  if (publicPresent) {
    publicActions.push("browse all content");
    if (searchPresent) publicActions.push("search");
    if (filterPresent) publicActions.push("filter");
    if (printPresent) publicActions.push("print results");
  }
  if (publicActions.length > 0) {
    assumptions.push(
      `Public users can: ${publicActions.join(", ")}, without logging in.`,
    );
  }

  // 6. Admin actions
  if (adminPresent && crudPresent) {
    assumptions.push(
      "Admin can add, edit, delete and manage records through a secure admin interface.",
    );
  }

  // 7. Website links
  if (linksPresent) {
    assumptions.push(
      "Website links should open in a new tab and must be validated as proper URLs before saving.",
    );
  }

  // 8. Recommended V1 additions (confirmable, not silently injected)
  if (adminPresent && crudPresent) {
    const recommended: string[] = [
      "active/inactive status for each record",
      "display order to control sort position",
      "optional internal admin notes per record",
    ];
    if (!exportPresent) {
      recommended.push("CSV export for admin backup and portability");
    }
    assumptions.push(
      `Recommended V1 additions to confirm: ${recommended.join(", ")}.`,
    );
  }

  // 9. If CSV/export already mentioned, just note it
  if (exportPresent && adminPresent) {
    assumptions.push(
      "CSV export or download has been mentioned - include this as an admin-only feature in V1.",
    );
  }

  // 10. Out of scope
  const outOfScope: string[] = [];
  if (!aiPresent) outOfScope.push("no AI features");
  if (!paymentsPresent) outOfScope.push("no payments or billing");
  if (!bookingPresent) outOfScope.push("no booking or scheduling");
  outOfScope.push("no public user accounts or registration");
  outOfScope.push("no email notifications");
  outOfScope.push("no comments, ratings, reviews or public submissions");
  if (outOfScope.length > 0) {
    assumptions.push(`Out of scope for V1: ${outOfScope.join(", ")}.`);
  }

  // 11. Target builder
  if (isReplit) {
    assumptions.push(
      "Target builder: Replit. The prompt must be Replit-ready with admin security, live preview testing, mobile optimisation, external link QA and clear handover notes.",
    );
  } else {
    assumptions.push(
      `Target builder: ${targetBuilderLabel(input.targetBuilder)}. The prompt should be platform-neutral and portable to any AI builder.`,
    );
  }

  // 12. Mobile
  assumptions.push(
    "Include mobile optimisation and responsive design for all screens, minimum 320px width.",
  );

  return assumptions;
}

// ---------------------------------------------------------------------------
// Cleaning utilities (used before passing to the backend)
// ---------------------------------------------------------------------------

export function cleanAssumptions(assumptions: string[]): string[] {
  return assumptions
    .map((a) => a.trim())
    .filter((a) => a.length > 0)
    .slice(0, MAX_ASSUMPTIONS)
    .map((a) => (a.length > MAX_ASSUMPTION_LENGTH ? a.slice(0, MAX_ASSUMPTION_LENGTH) : a));
}
